import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("children");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [children, setChildren] = useState([]);
  const [applications, setApplications] = useState([]);
  const [donations, setDonations] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);

  // Check for admin token
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) navigate("/login");
  }, [navigate]);

  // Fetch data
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    fetch("https://lightstaracademy.pythonanywhere.com/api/admin/children/", { headers })
      .then((res) => res.json())
      .then(setChildren)
      .catch(console.error);

    fetch("https://lightstaracademy.pythonanywhere.com/api/admin/applications/", { headers })
      .then((res) => res.json())
      .then(setApplications)
      .catch(console.error);

    fetch("https://lightstaracademy.pythonanywhere.com/api/admin/donations/", { headers })
      .then((res) => res.json())
      .then(setDonations)
      .catch(console.error);

    fetch("https://lightstaracademy.pythonanywhere.com/api/admin/sponsorships/", { headers })
      .then((res) => res.json())
      .then(setSponsorships)
      .catch(console.error);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`
          fixed md:static top-0 left-0 h-full w-64 bg-[#0077C8] text-white z-50 transform
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 transition-transform duration-300 ease-in-out
          flex flex-col justify-between
        `}
      >
        {/* Sidebar Header */}
        <div className="p-6 border-b border-blue-900 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Admin Menu</h2>
          {/* Close button on mobile */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="md:hidden focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2"
              viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex flex-col p-4 space-y-2 flex-grow">
          <button
            className={`text-left px-4 py-2 rounded hover:bg-blue-900 ${activeTab === "children" ? "bg-blue-900 font-bold" : ""}`}
            onClick={() => { setActiveTab("children"); setIsSidebarOpen(false); }}
          >
            📋 Children
          </button>
          <button
            className={`text-left px-4 py-2 rounded hover:bg-blue-900 ${activeTab === "applications" ? "bg-blue-900 font-bold" : ""}`}
            onClick={() => { setActiveTab("applications"); setIsSidebarOpen(false); }}
          >
            📝 Applications
          </button>
          <button
            className={`text-left px-4 py-2 rounded hover:bg-blue-900 ${activeTab === "donations" ? "bg-blue-900 font-bold" : ""}`}
            onClick={() => { setActiveTab("donations"); setIsSidebarOpen(false); }}
          >
            💰 Donations
          </button>
          <button
            className={`text-left px-4 py-2 rounded hover:bg-blue-900 ${activeTab === "sponsorships" ? "bg-blue-900 font-bold" : ""}`}
            onClick={() => { setActiveTab("sponsorships"); setIsSidebarOpen(false); }}
          >
            🤝 Sponsorships
          </button>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-blue-900">
          <button
            onClick={() => { handleLogout(); setIsSidebarOpen(false); }}
            className="w-full text-left px-4 py-2 rounded bg-red-500 hover:bg-red-600 transition font-semibold"
          >
            🔓 Logout
          </button>
        </div>
      </aside>

      {/* Header */}
      <header className="bg-[#0077C8] text-white px-4 py-4 flex items-center justify-between md:justify-center relative md:hidden">
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="focus:outline-none"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2"
            viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-center flex-1">Lightstar Academy Admin Panel</h1>
      </header>

      {/* Content */}
      <main className="flex-1 p-6 overflow-auto">
        {activeTab === "children" && (
          <section>
            <h2 className="text-2xl font-semibold mb-4">Children Profiles</h2>
            <div className="overflow-x-auto rounded-lg border">
              <table className="min-w-full border-collapse">
                <thead className="bg-[#E6F4FA]">
                  <tr>
                    <th className="px-4 py-2 text-left">ID</th>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Age</th>
                    <th className="px-4 py-2 text-left">Sex</th>
                    <th className="px-4 py-2 text-left">Country</th>
                    <th className="px-4 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(children) && children.length > 0 ? (
                    children.map((child) => (
                      <tr key={child.id} className="border-b hover:bg-[#F9FAFB]">
                        <td className="px-4 py-2">{child.id}</td>
                        <td className="px-4 py-2">{child.name}</td>
                        <td className="px-4 py-2">{child.age}</td>
                        <td className="px-4 py-2">{child.sex}</td>
                        <td className="px-4 py-2">{child.country}</td>
                        <td className="px-4 py-2">
                          <button className="text-[#0077C8] hover:underline">Edit</button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center p-4 text-gray-500">No children found or failed to load.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === "applications" && (
          <section>
            <h2 className="text-2xl font-semibold mb-4">Applications</h2>
            <ul className="divide-y">
              {Array.isArray(applications) && applications.length > 0 ? (
                applications.map((app) => (
                  <li key={app.id} className="py-4 flex justify-between items-center">
                    <span className="text-gray-800">{app.child_name} — Status: {app.status}</span>
                    <button className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700">
                      Verify
                    </button>
                  </li>
                ))
              ) : (
                <li className="text-center py-6 text-gray-500">No applications found.</li>
              )}
            </ul>
          </section>
        )}

        {activeTab === "donations" && (
          <section>
            <h2 className="text-2xl font-semibold mb-4">Donations</h2>
            <ul className="divide-y">
              {donations.length > 0 ? (
                donations.map((don) => (
                  <li key={don.id} className="py-4 flex justify-between">
                    <span className="text-gray-800">{don.donor_name} — ${don.amount}</span>
                    <span className="text-gray-500">{don.date}</span>
                  </li>
                ))
              ) : (
                <li className="text-center py-6 text-gray-500">No donations found.</li>
              )}
            </ul>
          </section>
        )}

        {activeTab === "sponsorships" && (
          <section>
            <h2 className="text-2xl font-semibold mb-4">Sponsorships</h2>
            <ul className="divide-y">
              {sponsorships.length > 0 ? (
                sponsorships.map((spon) => (
                  <li key={spon.id} className="py-4 flex justify-between">
                    <span className="text-gray-800">{spon.child_name} — Sponsor: {spon.sponsor_name}</span>
                    <span className="text-gray-500">Active: {spon.active ? "Yes" : "No"}</span>
                  </li>
                ))
              ) : (
                <li className="text-center py-6 text-gray-500">No sponsorships found.</li>
              )}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
